/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ContactDeliveryMethod,
  QuotationPrintMethod,
  QuotationVariationDto,
} from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isEntityInvalid,
  queryVersion,
  setEntityFields,
  Table,
  whenModified,
} from '@gh/common-shared';
import {
  EntityVersion,
  ID,
  PrintEntryPoint,
  WithDirty,
  WithFormMode,
  WithRecalculationNeeded,
  WithVersion,
} from '@gh/common-shared-data';
import { FormMode, isCreateFormMode, isEditFormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { flow, isEmpty } from 'lodash';
import { SelectionModel } from '@gh/core-data';
import {
  QuoteDetailsBottomInfo,
  QuoteDetailsDerivedInfo,
  HlpQuoteDetailsInitResponse,
  HlpQuoteDetailsMasterInfo,
  QuoteDetailsSaveRequest,
  QuotePrintFilter,
  QuotePrintRequest,
  QuoteVariation,
  quoteVariationTable,
  validateQuoteMasterInfoForCreate,
  validateQuoteMasterInfoForEdit,
} from '../../../resources/hlp-quote.model';

export enum QuoteDetailsStateEventType {
  Load = 'Load',
  MergeOnSave = 'MergeOnSave',
  MergeOnRecalculate = 'MergeOnRecalculate',
  SetMasterInfoShadow = 'SetMasterInfoShadow',
  CommitMasterInfo = 'CommitMasterInfo',
  VariationAddSet = 'VariationAddSet',
  VariationSetField = 'VariationSetField',
  VariationRemove = 'VariationRemove',
  SetBottomField = 'SetBottomField',
  VariationMoveCreditDebit = 'VariationMoveCreditDebit',
  NavigateToInvalid = 'NavigateToInvalid',
  NavigateToNotPriceBookVariation = 'NavigateToNotPriceBookVariation',
  ResetFocusedCell = 'ResetFocusedCell',
  Approved = 'Approved',
  Unapproved = 'Unapproved',
  UpdateVersion = 'UpdateVersion',
  SetProfitVisibility = 'SetProfitVisibility',
  ChangeVariationListFilter = 'ChangeVariationListFilter',
  SetVariationSelection = 'SetVariationSelection',
}

export type QuoteDetailsStateLoadEvent = {
  type: QuoteDetailsStateEventType.Load;
  init: HlpQuoteDetailsInitResponse;
  supportsProfit: boolean;
  isProfitVisible: boolean;
  mode?: FormMode;
};

export type QuoteDetailsStateMergeOnSaveEvent = {
  type: QuoteDetailsStateEventType.MergeOnSave;
  saveResponse: HlpQuoteDetailsInitResponse;
};

export type QuoteDetailsStateMergeOnRecalculateEvent = {
  type: QuoteDetailsStateEventType.MergeOnRecalculate;
  saveResponse: HlpQuoteDetailsInitResponse;
};

export type QuoteDetailsStateSetMasterInfoShadowEvent = {
  type: QuoteDetailsStateEventType.SetMasterInfoShadow;
  snapshot: FormStateSnapshot<HlpQuoteDetailsMasterInfo>;
  derivedInfo?: QuoteDetailsDerivedInfo;
};

export type QuoteDetailsStateCommitMasterInfoEvent = {
  type: QuoteDetailsStateEventType.CommitMasterInfo;
  resetVariations?: boolean;
};

export type QuoteDetailsStateVariationAddSetEvent = {
  type: QuoteDetailsStateEventType.VariationAddSet;
  variations: QuoteVariation[];
};

export type QuoteDetailsStateVariationSetFieldEvent = {
  type: QuoteDetailsStateEventType.VariationSetField;
  variationId: ID;
  name: string;
  value: any;
};

export type QuoteDetailsStateVariationRemoveEvent = {
  type: QuoteDetailsStateEventType.VariationRemove;
  variationId: ID;
};

export type QuoteDetailsStateSetBottomFieldEvent = {
  type: QuoteDetailsStateEventType.SetBottomField;
  name: keyof QuoteDetailsBottomInfo;
  value: any;
};

export type QuoteDetailsStateVariationMoveCreditDebitEvent = {
  type: QuoteDetailsStateEventType.VariationMoveCreditDebit;
  variationId: ID;
};

export type QuoteDetailsStateChangeVariationFilterEvent = {
  type: QuoteDetailsStateEventType.ChangeVariationListFilter;
  variationsFilter: VariationsFilter;
};

export type QuoteDetailsStateNavigateToInvalidEvent = {
  type: QuoteDetailsStateEventType.NavigateToInvalid;
};

export type QuoteDetailsStateNavigateToNotPriceBookVariationEvent = {
  type: QuoteDetailsStateEventType.NavigateToNotPriceBookVariation;
};

export type QuoteDetailsStateResetFocusedCellEvent = {
  type: QuoteDetailsStateEventType.ResetFocusedCell;
};

export type QuoteDetailsStateApprovedEvent = {
  type: QuoteDetailsStateEventType.Approved;
  details: HlpQuoteDetailsInitResponse;
};

export type QuoteDetailsStateUnapprovedEvent = {
  type: QuoteDetailsStateEventType.Unapproved;
  details: HlpQuoteDetailsInitResponse;
};

export type QuoteDetailsStateUpdateVersionEvent = {
  type: QuoteDetailsStateEventType.UpdateVersion;
  version: EntityVersion;
};

export type QuoteDetailsStateSetProfitVisibility = {
  type: QuoteDetailsStateEventType.SetProfitVisibility;
  visible: boolean;
};

export type QuoteDetailsStateSetVariationSelectionEvent = {
  type: QuoteDetailsStateEventType.SetVariationSelection;
  selectionModel: SelectionModel;
};

export type QuoteDetailsStateEvent = QuoteDetailsStateLoadEvent
  | QuoteDetailsStateMergeOnSaveEvent
  | QuoteDetailsStateMergeOnRecalculateEvent
  | QuoteDetailsStateSetMasterInfoShadowEvent
  | QuoteDetailsStateCommitMasterInfoEvent
  | QuoteDetailsStateVariationAddSetEvent
  | QuoteDetailsStateVariationSetFieldEvent
  | QuoteDetailsStateVariationRemoveEvent
  | QuoteDetailsStateSetBottomFieldEvent
  | QuoteDetailsStateVariationMoveCreditDebitEvent
  | QuoteDetailsStateNavigateToInvalidEvent
  | QuoteDetailsStateNavigateToNotPriceBookVariationEvent
  | QuoteDetailsStateResetFocusedCellEvent
  | QuoteDetailsStateApprovedEvent
  | QuoteDetailsStateUnapprovedEvent
  | QuoteDetailsStateUpdateVersionEvent
  | QuoteDetailsStateSetProfitVisibility
  | QuoteDetailsStateChangeVariationFilterEvent
  | QuoteDetailsStateSetVariationSelectionEvent;

export type QuoteDetailsSelection = {
  focusedCell?: FocusedCell;
  variationSelection?: SelectionModel;
};

export type QuoteDetailsSettings = {
  isSalesEstimateShown: boolean;
  isQuickQuotesAllowed: boolean;
  isGtoTopExist: boolean;
  useSalesEstimate: boolean;
  supportsProfit: boolean;
  isProfitVisible: boolean;
};

export type VariationsFilter = {
  search: string;
};

export type HlpQuoteDetailsState = WithVersion<WithFormMode<WithRecalculationNeeded<WithDirty<{
  derivedInfo: QuoteDetailsDerivedInfo;
  masterInfo: FormStateSnapshot<HlpQuoteDetailsMasterInfo>;
  bottom: QuoteDetailsBottomInfo;
  // shadow is used to store current form state snapshot
  // when form state is applied it is stored in the masterInfo field
  masterInfoShadow: FormStateSnapshot<HlpQuoteDetailsMasterInfo>;
  variations: Table<QuoteVariation>;
  variationsFilter: VariationsFilter;
  selection: QuoteDetailsSelection;
  settings: QuoteDetailsSettings;
  method: QuotationPrintMethod;
}>>>>;

export const QUOTE_DETAILS_INIT_STATE: HlpQuoteDetailsState = {
  derivedInfo: <QuoteDetailsDerivedInfo>{},
  bottom: {
    memo: '',
    isMemoOnTop: false,
  },
  masterInfo: <FormStateSnapshot<HlpQuoteDetailsMasterInfo>>{},
  masterInfoShadow: <FormStateSnapshot<HlpQuoteDetailsMasterInfo>>{},
  variations: quoteVariationTable.create(),
  formMode: FormMode.ViewMode,
  isDirty: false,
  isRecalculationNeeded: false,
  version: -1,
  selection: {},
  settings: {
    isSalesEstimateShown: false,
    isQuickQuotesAllowed: false,
    isGtoTopExist: false,
    useSalesEstimate: false,
    supportsProfit: false,
    isProfitVisible: false,
  },
  method: QuotationPrintMethod.PrintQuote,
  variationsFilter: { search: '' },
};

/**
 * Merges sales price and price without GST (for au or nz region) into derived info
 */
export const calculateTopDerivedInfo = (derived: QuoteDetailsDerivedInfo, pbSalesPrice: number = 0, pbPrice = 0) =>
  setEntityFields(derived, { pbSalesPrice, pbPrice });

export const queryQuoteDerivedInfo = (state: HlpQuoteDetailsState) => state.derivedInfo;
export const queryQuoteIsProduced = (state: HlpQuoteDetailsState) => isNotNil(queryQuoteDerivedInfo(state).rfqNumber);
export const queryQuoteIsApproved = (state: HlpQuoteDetailsState) => isNotNil(queryQuoteDerivedInfo(state).approvedDate);
export const queryQuoteBottomInfo = (state: HlpQuoteDetailsState) => state.bottom;
export const queryQuoteTopFormHasErrors = (state: HlpQuoteDetailsState) =>
  isCreateFormMode(state.formMode)
  && isNotNil(validateQuoteMasterInfoForCreate(getFormStateSnapshotValue(queryQuoteMasterInfo(state))))
  ||
  isEditFormMode(state.formMode)
  && isNotNil(validateQuoteMasterInfoForEdit(getFormStateSnapshotValue(queryQuoteMasterInfo(state))));
export const queryQuoteInvalidVariations = (state: HlpQuoteDetailsState) =>
  queryQuoteVariations(state).filter(isEntityInvalid);
export const queryQuoteVariationsHasErrors = (state: HlpQuoteDetailsState) =>
  queryQuoteInvalidVariations(state).length > 0;
export const queryQuoteVariationsFilter = (state: HlpQuoteDetailsState) =>
  state.variationsFilter;
export const queryQuoteHasErrors = (state: HlpQuoteDetailsState) =>
  queryQuoteTopFormHasErrors(state) || queryQuoteVariationsHasErrors(state);
export const queryQuoteMasterInfo = (state: HlpQuoteDetailsState) => state.masterInfo;
export const queryQuotationBase = (state: HlpQuoteDetailsState) =>
  getFormStateSnapshotValue(queryQuoteMasterInfo(state)).base;
export const queryQuotePriceBook = (state: HlpQuoteDetailsState) => getFormStateSnapshotValue(state.masterInfo).priceBook;
export const queryQuoteHousePlan = (state: HlpQuoteDetailsState) => getFormStateSnapshotValue(state.masterInfo).housePlan;
export const queryQuoteBaseHousePlan = (state: HlpQuoteDetailsState) =>
  getFormStateSnapshotValue(state.masterInfo).baseHousePlan;
export const queryQuoteMasterInfoShadow = (state: HlpQuoteDetailsState) => state.masterInfoShadow;
export const queryQuoteVariations = (state: HlpQuoteDetailsState) =>
  quoteVariationTable.byOrder(state.variations, 'existing');
export const queryQuoteDirtyVariations = (state: HlpQuoteDetailsState) =>
  quoteVariationTable.all(state.variations).filter(whenModified);
export const queryQuoteVariation = (state: HlpQuoteDetailsState, id: ID) => quoteVariationTable.byId(state.variations, id);
export const queryQuoteVariationFocusedCell = (state: HlpQuoteDetailsState) => state.selection.focusedCell;
export const queryQuoteVariationSelection = (state: HlpQuoteDetailsState) => state.selection.variationSelection;
export const queryQuoteSettings = (state: HlpQuoteDetailsState) => state.settings;
export const queryQuoteIsProfitSupported = (state: HlpQuoteDetailsState) => queryQuoteSettings(state).supportsProfit;
export const queryQuoteIsProfitVisible = (state: HlpQuoteDetailsState) => queryQuoteSettings(state).isProfitVisible;
export const queryQuoteNotPriceBookVariations = (state: HlpQuoteDetailsState) =>
  queryQuoteVariations(state).filter((variation) => !variation.isInPriceBook);
export const queryQuoteHasNotPriceBookVariations = flow(queryQuoteNotPriceBookVariations, isEmpty);
const queryQuoteSaveOrRecalculateModel = (state: HlpQuoteDetailsState, isPersist: boolean) => {
  const masterInfo = getFormStateSnapshotValue(queryQuoteMasterInfo(state));

  return <QuoteDetailsSaveRequest>{
    priceBookId: masterInfo.priceBook!.id,
    description: masterInfo.description,
    isRounded: masterInfo.isRounded,
    base: masterInfo.base,
    siteId: masterInfo.site && masterInfo.site.id,
    baseHousePlanId: masterInfo.baseHousePlan && masterInfo.baseHousePlan!.housePlanId,
    salesTaxRates: masterInfo.salesTaxRates,
    memo: state.bottom.memo,
    isMemoOnTop: state.bottom.isMemoOnTop,
    version: state.version,
    variations: queryQuoteDirtyVariations(state).map((variation) => ({
      quotationItemId: variation.quotationItemId,
      itemId: variation.itemId,
      isComponent: variation.isComponent,
      description: variation.description,
      costCenterId: variation.costCenterId,
      quantity: variation.quantity,
      origQuantity: variation.origQuantity,
      price: variation.price,
      itemPrice: variation.itemPrice,
      isCredit: variation.isCredit,
      dirtyType: variation.dirtyType,
    })),
    isPersist,
  };
};

export const queryQuoteToHlpModel = (state: HlpQuoteDetailsState) => {
  const masterInfo = state.masterInfo.value;
  const baseHousePlan = masterInfo.baseHousePlan;
  const priceBook = masterInfo.priceBook;
  return {
    quoteDescription: masterInfo.description,
    isRounded:        masterInfo.isRounded,
    base:             masterInfo.base,
    priceBookId:      priceBook?.id,
    priceBookName:    priceBook?.name,
    baseHousePlanId:  baseHousePlan?.housePlanId,
  };
};

export const queryQuoteToHlpModelWithVariations = (state: HlpQuoteDetailsState) => {
  const masterInfo = state.masterInfo.value;
  const baseHousePlan = masterInfo.baseHousePlan;
  const priceBook = masterInfo.priceBook;
  return {
    quoteDescription: masterInfo.description,
    isRounded:        masterInfo.isRounded,
    base:             masterInfo.base,
    priceBookId:      priceBook?.id,
    priceBookName:    priceBook?.name,
    baseHousePlanId:  baseHousePlan?.housePlanId,
    salesTaxRates:    masterInfo.salesTaxRates,
    variations:       queryQuoteDirtyVariations(state).map((item: QuotationVariationDto) => {
      const variation = {
        quotationItemId: item.quotationItemId,
        isComponent:     item.isComponent,
        description:     item.description,
        costCenterId:    item.costCenterId,
        quantity:        item.quantity,
        origQuantity:    item.origQuantity,
        price:           item.price,
        itemPrice:       item.itemPrice,
        isCredit:        item.isCredit,
        itemId:          item.itemId,
// @ts-expect-error Legacy typing suppression
        dirtyType: item.dirtyType,

      };
      return variation;
    }),
  };
};
export const queryQuoteSaveModel = (state: HlpQuoteDetailsState) => queryQuoteSaveOrRecalculateModel(state, true);
export const queryQuoteRecalculateModel = (state: HlpQuoteDetailsState) => queryQuoteSaveOrRecalculateModel(state, false);
export const queryQuotePrintProduceRequest = (state: HlpQuoteDetailsState,
                                              filter: QuotePrintFilter) => <QuotePrintRequest>({
  type: getFormStateSnapshotValue(queryQuoteMasterInfo(state)).base,
  isShowBasePrice: filter.isShowBasePrice,
  isForEstimator: filter.isForEstimator,
  isAuthorityToProceedRequired: filter.isAuthorityToProceedRequired,
  isPrintTakeOff: filter.isPrintTakeOff,
  method: filter.deliveryBy === ContactDeliveryMethod.Email
    ? QuotationPrintMethod.SendByEmail
    : QuotationPrintMethod.PrintQuote,
  version: queryVersion(state),
  entryPoint: PrintEntryPoint.Produce,
});
